import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BadgeCheck, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { CEFR_LEVELS } from "../mock";

const scales = [
  { test: "Redesigned TOEIC® Bridge Listening & Reading", section: "Listening / Reading", perSection: "15 – 50", total: "30 – 100", cefr: "A1 – B1" },
  { test: "TOEIC® Listening & Reading", section: "Listening / Reading", perSection: "5 – 495", total: "10 – 990", cefr: "A1 – C1" },
  { test: "TOEIC® Speaking & Writing", section: "Speaking / Writing", perSection: "0 – 200", total: "0 – 400", cefr: "A1 – C1" },
];

const links = [
  { label: "ETS Global — Understanding TOEIC Test Scores", href: "https://www.etsglobal.org/ls/en/content/understanding-toeic-tests-scores" },
  { label: "ETS — Redesigned TOEIC Bridge Test Official Page", href: "https://www.ets.org/toeic-bridge.html" },
  { label: "Council of Europe — CEFR Framework", href: "https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions" },
  { label: "WORC — Workforce Opportunities & Residency Cayman", href: "https://www.worc.ky" },
];

const cefrLinks = [
  { label: "TOEIC® Listening & Reading Tests", href: "https://www.ets.org/pdfs/toeic/toeic-mapping-cefr-reference.pdf" },
  { label: "TOEIC® Speaking and Writing Tests", href: "https://www.ets.org/pdfs/toeic/toeic-mapping-cefr-reference.pdf" },
  { label: "TOEIC Bridge® Test", href: "https://www.etsglobal.org/fx/en/content/understanding-toeic-tests-scores" },
  { label: "Redesigned TOEIC Bridge® Tests", href: "https://www.ets.org/pdfs/toeic/toeic-bridge-mapping-cefr-reference.pdf" },
];

export default function Scores() {
  return (
    <div data-testid="scores-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Understanding Your Results</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">What Your Test Scores Mean</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            TOEIC® scores correlate to the Common European Framework of Reference for Languages (CEFR) — the international standard for describing language ability. Scores are valid for two years from the test date.
          </p>
        </div>
      </div>

      {/* CEFR correlation links */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] tracking-tight text-center mb-8">
            Click on a test below to view the relevant CEFR level that the scores are correlated to:
          </h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {cefrLinks.map((l) => (
              <div key={l.label} className="flex flex-wrap items-center gap-2 text-lg">
                <span className="italic text-[#0A2540] font-medium">{l.label}</span>
                <span className="text-slate-400">–</span>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cefr-link-${l.label.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  className="font-extrabold text-[#0E9AAA] hover:text-[#0C8695] hover:underline inline-flex items-center gap-1.5 transition-colors"
                >
                  CLICK HERE <ExternalLink size={15} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORC requirement */}
      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-[#E8F7F9] border border-[#B5E6EC] rounded-2xl p-8 flex flex-col sm:flex-row items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-[#0E9AAA] flex items-center justify-center shrink-0">
              <BadgeCheck className="text-white" size={24} />
            </div>
            <div>
              <h2 className="font-extrabold text-[#0A2540] text-xl">Work Permit Requirement (WORC)</h2>
              <p className="mt-2.5 text-slate-700 leading-relaxed">
                For Cayman Islands work permit applications, the Workforce Opportunities and Residency Cayman (WORC)
                department requires a minimum score of <strong>Level A2 in the Listening category</strong> and{" "}
                <strong>Level A1 in the Reading category</strong> on the Redesigned TOEIC Bridge Listening & Reading test —
                that is a minimum of <strong>26 in the Listening section</strong> and <strong>19 in the Reading section</strong>.
              </p>
              <Link to="/book?test=bridge-lr" className="inline-block mt-4">
                <Button size="sm" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg">
                  Book the Bridge Test <ArrowRight size={15} className="ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Score scales */}
      <section className="pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[#0A2540] tracking-tight mb-6">Score Scales by Test</h2>
          <Card className="rounded-2xl border-slate-100 shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F6FAFB]">
                  <TableHead className="font-extrabold text-[#0A2540]">Test</TableHead>
                  <TableHead className="font-extrabold text-[#0A2540]">Sections</TableHead>
                  <TableHead className="font-extrabold text-[#0A2540]">Score per Section</TableHead>
                  <TableHead className="font-extrabold text-[#0A2540]">Total Score</TableHead>
                  <TableHead className="font-extrabold text-[#0A2540]">CEFR Range</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scales.map((s) => (
                  <TableRow key={s.test}>
                    <TableCell className="font-bold text-[#0A2540]">{s.test}</TableCell>
                    <TableCell className="text-slate-600">{s.section}</TableCell>
                    <TableCell className="text-slate-600">{s.perSection}</TableCell>
                    <TableCell className="text-slate-600">{s.total}</TableCell>
                    <TableCell><span className="px-2.5 py-1 rounded-full bg-[#E8F7F9] text-[#0C8695] text-xs font-bold">{s.cefr}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </section>

      {/* CEFR levels */}
      <section className="py-16 bg-[#F6FAFB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[#0A2540] tracking-tight mb-2">The CEFR Levels Explained</h2>
          <p className="text-slate-600 mb-8">Each TOEIC score maps to one of these internationally recognized proficiency levels.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CEFR_LEVELS.map((l) => (
              <Card key={l.level} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-lg transition-shadow">
                <CardContent className="p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-[#0A2540] flex items-center justify-center text-white font-extrabold">{l.level}</div>
                    <div className="font-bold text-[#0A2540]">{l.label}</div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{l.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Links */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-extrabold text-[#0A2540] tracking-tight mb-6">Helpful Official Resources</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 bg-[#F6FAFB] border border-slate-100 rounded-xl px-6 py-4 font-semibold text-[#0A2540] hover:border-[#0E9AAA] hover:text-[#0E9AAA] transition-colors"
              >
                {l.label} <ExternalLink size={16} className="shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
